import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import moment from "moment";
import { REPORT_LINKS } from "../../../../utils/dashboardLinks";
import SearchTermReportDashboardTable from "../../../../modules/str/SearchTermReportDashboardTable";
import { Flex } from "../../../../../styled-system/jsx";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import { idsToVisibilityState } from "../../../../components/table/utils";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { useDebounce } from "@reactuses/core";
import { useGetV4SearchTermPerformanceByDate } from "../../../../modules/str/api/v4SearchTermPerformanceByDate";
import { useQuery } from "@tanstack/react-query";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4SearchTermPerformanceTotals } from "../../../../modules/str/api/v4SearchTermPerformanceReportTotals";
import useQueryParams from "../../../../hooks/useQueryParams";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import useSearchTermReportColumns from "../../../../modules/str/useSearchTermReportColumns";
import { Tabs, TabList, Tab } from "../../../../components/core/basic/Tabs";
import {
  DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS,
  DIMENSION_TYPES,
  SEARCH_TERM_DIMENSIONS,
} from "../../../../modules/str/utils/reportingConstants";

const SearchTermReportBody = () => {
  const { t } = useTranslation(["str", "perf_reports"]);
  const acosOrRoas = useAcosOrRoas();
  const [dimensions, setDimensions] = useQueryParams("dims", SEARCH_TERM_DIMENSIONS.QUERY);
  const [targetType, setTargetType] = useQueryParams("target_type", "keyword");
  const safeDimensions = dimensions || SEARCH_TERM_DIMENSIONS.KEYWORD_TEXT_WITH_MATCH;

  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const standardMetricIds = useStandardEntityMetrics("searchTerm");

  const categoriesQuery = useCategoriesQuery();

  const categoryFilterOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );

  const columns = useSearchTermReportColumns(safeDimensions);

  const defaultSelectedColumns = DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS;

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
      acos: acosOrRoas(true, false),
      roas: acosOrRoas(false, true),
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["keyword_text", "query"],
      end: [],
    },
    sorting: [{ id: "impressions", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchTermReport-${safeDimensions}`,
    initialState: initialStateConfig,
  });

  const flattenedReportFilters = useMemo(() => {
    const advTargetIdsFilter =
      dimensions?.includes(DIMENSION_TYPES.TARGET_ID) ?
        [{ id: "adv_target_ids_count", value: { value: 0, op: ">" } }]
      : [];
    const advKeywordIdsFilter =
      dimensions?.includes(DIMENSION_TYPES.KEYWORD_ID) ?
        [{ id: "adv_keyword_ids_count", value: { value: 0, op: ">" } }]
      : [];
    return [...advTargetIdsFilter, ...advKeywordIdsFilter];
  }, [dimensions]);

  const filters = useMemo(() => {
    const normalizedColumnFilters = state.columnFilters.map((f) => {
      return {
        id: f.id,
        value: f.value,
      };
    });

    return [...normalizedColumnFilters, ...flattenedReportFilters];
  }, [flattenedReportFilters, state.columnFilters]);

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const byDateOptionsGetter = useGetV4SearchTermPerformanceByDate();
  const searchTermPerformanceByDateQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );
  const totalOptionsGetter = useGetV4SearchTermPerformanceTotals();
  const searchTermPerformanceTotalsQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );

  const metrics = useMemo(() => {
    return standardMetricIds.map((id) => getMetricSpec(id));
  }, [standardMetricIds]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"searchTermPerformanceReport"}
        title={t(($) => $.perf_reports["Performance Overview"])}
        totals={{
          data: searchTermPerformanceTotalsQuery.data?.data || [],
          loading: searchTermPerformanceTotalsQuery.isFetching || searchTermPerformanceTotalsQuery.isPlaceholderData,
        }}
        timeline={{
          data: searchTermPerformanceByDateQuery.data?.data || [],
          loading: searchTermPerformanceByDateQuery.isFetching || searchTermPerformanceByDateQuery.isPlaceholderData,
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend"]}
        group={group}
        showAnnotations={false}
        byline={
          <>
            {longDate.format(moment(start).toDate())}
            {" - "}
            {longDate.format(moment(end).endOf("week").toDate())}
          </>
        }
        context={"searchTerm"}
      >
        <Flex>
          <Tabs
            selectedKey={safeDimensions}
            onSelectionChange={(key) => {
              const tab = key as string;
              if (tab) {
                if (tab.includes(DIMENSION_TYPES.TARGET_ID) || tab.includes(DIMENSION_TYPES.KEYWORD_ID)) {
                  setTargetType(tab.includes(DIMENSION_TYPES.TARGET_ID) ? "target" : "keyword");
                }
                setDimensions(tab);
              }
            }}
          >
            <TabList>
              <Tab id={SEARCH_TERM_DIMENSIONS.QUERY}>{t(($) => $.str["Queries"])}</Tab>
              <Tab id={SEARCH_TERM_DIMENSIONS.QUERY_MATCH_KEYWORD}>{t(($) => $.str["Keyword Target-Query Match"])}</Tab>
              <Tab id={SEARCH_TERM_DIMENSIONS.QUERY_MATCH_TARGET}>{t(($) => $.str["Product Target-Query Match"])}</Tab>
            </TabList>
          </Tabs>
        </Flex>
        <SearchTermReportDashboardTable
          state={state}
          initialState={initialState}
          setters={setters}
          dimensions={safeDimensions}
          categoryFilterOptions={categoryFilterOptions}
          targetType={targetType}
          parentFilters={flattenedReportFilters}
          key={safeDimensions}
          showBulkActionsButton={true}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

const SearchTermReportDashboard = () => {
  const { t } = useTranslation(["common"]);
  const breadCrumbItems = [
    {
      name: t(($) => $.common.sidebar["Search Term Report"]),
      href: "/user/report/str_report",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <SearchTermReportBody />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/report/str_report")({
  component: SearchTermReportDashboard,
  staticData: { application: "Reports" },
});
